import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useEffect, useState } from "react";

import API from "../../../api";

import {
  startToast,
  updateToastError,
  updateToastSuccess,
} from "../../../utils/notification-loading";

const Notifications = ({
  anomalyNotify = {},
  offlineNotify,
  setOfflineNotify,
}) => {
  const [anomaly, setAnomaly] = useState(anomalyNotify ?? {});

  const [offline, setOffline] = useState({
    since: offlineNotify?.since ?? 168,
    every: offlineNotify?.every ?? 24,
  });

  const [tiresiaChecked, setTiresiaChecked] = useState(false);
  const [tiresiaLevel, setTiresiaLevel] = useState(null);

  useEffect(() => {
    setAnomaly(anomalyNotify ?? {});
  }, [anomalyNotify]);

  useEffect(() => {
    if (offlineNotify) {
      setOffline({
        since: offlineNotify.since ?? 168,
        every: offlineNotify.every ?? 24,
      });
    }
  }, [offlineNotify]);

  useEffect(() => {
    API.getNotificationTiresia()
      .then((res) => {
        setTiresiaChecked(res.data?.enabled ?? false);
        setTiresiaLevel(res.data?.level ?? null);
      })
      .catch(() => {});
  }, []);

  const handleChangeNotify = (value, action) => {
    const ctrl = new AbortController();
    const { signal } = ctrl;

    startToast();

    API.updateNotify(
      {
        value: value,
        action: action ? "$addToSet" : "$pull",
      },
      signal
    )
      .then(() => {
        updateToastSuccess("Notification changed successfully");

        setAnomaly((prev) => ({
          ...prev,
          [value]: action,
        }));
      })
      .catch(() => {
        updateToastError("Error while changing notification");
      });
  };

  const handleChangeOffline = (value, type) => {
    const ctrl = new AbortController();
    const { signal } = ctrl;

    const payload = {
      ...offline,
      [type]: value,
    };

    startToast();

    API.setOfflineNotify(
      {
        value: payload,
      },
      signal
    )
      .then(() => {
        updateToastSuccess("Notification changed successfully");

        setOffline(payload);

        if (setOfflineNotify) {
          setOfflineNotify((prev) => ({
            ...(prev ?? {}),
            [type]: value,
          }));
        }
      })
      .catch(() => {
        updateToastError("Error while changing notification");
      });
  };

  const handleTiresiaClick = (level) => {
    startToast();

    API.updateNotificationTiresia({
      degree: level,
      enabled: true,
    })
      .then(() => {
        updateToastSuccess("Notification changed successfully");

        setTiresiaLevel(level);
        setTiresiaChecked(true);
      })
      .catch(() => {
        updateToastError("Error while changing notification");
      });
  };

  const handleTiresiaCheckbox = (checked) => {
    startToast();

    if (!checked) {
      API.updateNotificationTiresia({
        degree: "none",
        enabled: false,
      })
        .then(() => {
          updateToastSuccess("Notification changed successfully");

          setTiresiaChecked(false);
          setTiresiaLevel(null);
        })
        .catch(() => {
          updateToastError("Error while changing notification");
        });

      return;
    }

    API.updateNotificationTiresia({
      degree: "Medium",
      enabled: true,
    })
      .then(() => {
        updateToastSuccess("Notification changed successfully");

        setTiresiaChecked(true);
        setTiresiaLevel("Medium");
      })
      .catch(() => {
        updateToastError("Error while changing notification");
      });
  };

  return (
    <Box
      sx={{
        backgroundColor: "rgba(69, 144, 149, 0.05)",
        borderRadius: 2,
        border: "1px solid rgba(69, 144, 149, 0.1)",
        p: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: "#449095",
          mb: 2,
        }}
      >
        Notification Settings
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        divider={<Divider orientation="vertical" flexItem />}
        justifyContent="space-between"
      >
        {/* SECURITY EVENTS */}
        <Stack direction="column" spacing={2} width="100%">
          <Typography>Security Events</Typography>

          <FormGroup>
            <FormControlLabel
              label="High"
              control={
                <Checkbox
                  checked={anomaly?.[0] ?? false}
                  onChange={(e) =>
                    handleChangeNotify(0, e.target.checked)
                  }
                />
              }
            />

            <FormControlLabel
              label="Behaviors"
              control={
                <Checkbox
                  checked={anomaly?.[2] ?? false}
                  onChange={(e) =>
                    handleChangeNotify(2, e.target.checked)
                  }
                />
              }
            />

            <FormControlLabel
              label="Potential Threat"
              control={
                <Checkbox
                  checked={anomaly?.[3] ?? false}
                  onChange={(e) =>
                    handleChangeNotify(3, e.target.checked)
                  }
                />
              }
            />

            <FormControlLabel
              label="Tiresia"
              control={
                <Checkbox
                  checked={tiresiaChecked}
                  onChange={(e) =>
                    handleTiresiaCheckbox(e.target.checked)
                  }
                />
              }
            />

            {tiresiaChecked && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  ml: 1,
                  mt: 0.5,
                }}
              >
                <Tooltip
                  title="Notification output is restricted to high-priority events, minimizing notification volume."
                  placement="top"
                  arrow
                >
                  <Button
                    size="small"
                    variant={
                      tiresiaLevel === "High"
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                    }}
                    onClick={() => handleTiresiaClick("High")}
                  >
                    High
                  </Button>
                </Tooltip>

                <Tooltip
                  title="Notification output is more verbose and may generate a higher volume of notifications."
                  placement="top"
                  arrow
                >
                  <Button
                    size="small"
                    variant={
                      tiresiaLevel === "Medium"
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                    }}
                    onClick={() => handleTiresiaClick("Medium")}
                  >
                    Medium
                  </Button>
                </Tooltip>
              </Stack>
            )}
          </FormGroup>
        </Stack>

        {/* LECS OFFLINE */}
        <Stack direction="column" spacing={2} width="100%">
          <Typography>LECS Offline</Typography>

          <FormGroup
            sx={{
              width: "30%",
              minWidth: 180,
            }}
          >
            <Typography>Send since</Typography>

            <TextField
              select
              size="small"
              value={offline.since}
              onChange={(e) =>
                handleChangeOffline(e.target.value, "since")
              }
              sx={{
                mb: 2,
              }}
            >
              <MenuItem value={168}>7 Days</MenuItem>
              <MenuItem value={72}>3 Days</MenuItem>
              <MenuItem value={24}>24 Hours</MenuItem>
              <MenuItem value={12}>12 Hours</MenuItem>
            </TextField>

            <Typography>Send every</Typography>

            <TextField
              select
              size="small"
              value={offline.every}
              onChange={(e) =>
                handleChangeOffline(e.target.value, "every")
              }
            >
              <MenuItem value={1}>1 Hour</MenuItem>
              <MenuItem value={3}>3 Hours</MenuItem>
              <MenuItem value={6}>6 Hours</MenuItem>
              <MenuItem value={12}>12 Hours</MenuItem>
              <MenuItem value={24}>24 Hours</MenuItem>
            </TextField>
          </FormGroup>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Notifications;